import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, UploadSimple } from "@phosphor-icons/react";

function Section({ title, description, children }) {
  return (
    <div className="seva-card">
      <h2 className="font-display font-bold text-xl">{title}</h2>
      {description && <p className="text-sm text-[#56624F] mt-1 mb-5">{description}</p>}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-sm font-semibold">{label}</div>
      {hint && <div className="text-xs text-[#56624F] mb-1.5">{hint}</div>}
      {children}
    </label>
  );
}

const inputCls = "w-full px-4 py-2.5 rounded-xl border border-[#DCE5D4] bg-white";

function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(up.data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <Field label={label}>
      <div className="flex items-center gap-3">
        {value && <img src={value} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#DCE5D4]" />}
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} placeholder="Image URL" data-testid={testid} />
        <label className="btn-secondary !py-2.5 !px-3 cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 text-sm">
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          <UploadSimple size={16} /> {uploading ? "Uploading..." : "Upload"}
        </label>
      </div>
    </Field>
  );
}

export default function AdminWebsite() {
  const [pages, setPages] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/site-pages").then((r) => setPages(r.data));
  }, []);

  if (!pages) return null;

  const patch = (page, key, value) => setPages((p) => ({ ...p, [page]: { ...p[page], [key]: value } }));
  const patchStat = (i, key, value) => setPages((p) => {
    const stats = [...p.home.stats];
    stats[i] = { ...stats[i], [key]: value };
    return { ...p, home: { ...p.home, stats } };
  });
  const linesToArray = (text) => text.split("\n").map((l) => l.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/settings/site-pages", pages);
      toast.success("Website content saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl">Website</h1>
          <p className="text-[#56624F] mt-1">Edit the Home, About Us, and Contact Us pages live at seva-india.org</p>
        </div>
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="website-save"><FloppyDisk size={16} /> {saving ? "Saving..." : "Save Website"}</button>
      </div>

      <Section title="Home Page">
        <Field label="Hero Heading"><input value={pages.home.hero_heading} onChange={(e) => patch("home", "hero_heading", e.target.value)} className={inputCls} /></Field>
        <Field label="Hero Subheading"><textarea rows={2} value={pages.home.hero_subheading} onChange={(e) => patch("home", "hero_subheading", e.target.value)} className={inputCls} /></Field>
        <ImageField label="Hero Image (optional)" value={pages.home.hero_image_url} onChange={(v) => patch("home", "hero_image_url", v)} testid="home-hero-image" />

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Stats Eyebrow"><input value={pages.home.stats_eyebrow} onChange={(e) => patch("home", "stats_eyebrow", e.target.value)} className={inputCls} /></Field>
          <Field label="Stats Heading"><input value={pages.home.stats_heading} onChange={(e) => patch("home", "stats_heading", e.target.value)} className={inputCls} /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {pages.home.stats.map((s, i) => (
            <div key={i} className="border border-[#DCE5D4] rounded-xl p-3 space-y-2">
              <input value={s.value} onChange={(e) => patchStat(i, "value", e.target.value)} className={inputCls} placeholder="259+" />
              <input value={s.label} onChange={(e) => patchStat(i, "label", e.target.value)} className={inputCls} placeholder="Dogs Rescued" />
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Journey Eyebrow"><input value={pages.home.journey_eyebrow} onChange={(e) => patch("home", "journey_eyebrow", e.target.value)} className={inputCls} /></Field>
          <Field label="Journey Heading"><input value={pages.home.journey_heading} onChange={(e) => patch("home", "journey_heading", e.target.value)} className={inputCls} /></Field>
        </div>
        <Field label="Journey Body"><textarea rows={2} value={pages.home.journey_body} onChange={(e) => patch("home", "journey_body", e.target.value)} className={inputCls} /></Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Mission Eyebrow"><input value={pages.home.mission_eyebrow} onChange={(e) => patch("home", "mission_eyebrow", e.target.value)} className={inputCls} /></Field>
          <Field label="Mission Heading"><input value={pages.home.mission_heading} onChange={(e) => patch("home", "mission_heading", e.target.value)} className={inputCls} /></Field>
        </div>
        <Field label="Mission Body" hint="Separate paragraphs with a blank line"><textarea rows={5} value={pages.home.mission_body} onChange={(e) => patch("home", "mission_body", e.target.value)} className={inputCls} /></Field>
      </Section>

      <Section title="About Us Page">
        <Field label="Hero Heading"><input value={pages.about.hero_heading} onChange={(e) => patch("about", "hero_heading", e.target.value)} className={inputCls} /></Field>
        <Field label="Hero Subheading"><textarea rows={2} value={pages.about.hero_subheading} onChange={(e) => patch("about", "hero_subheading", e.target.value)} className={inputCls} /></Field>
        <ImageField label="Hero Background Image (optional)" value={pages.about.hero_image_url} onChange={(v) => patch("about", "hero_image_url", v)} testid="about-hero-image" />

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Goal Eyebrow"><input value={pages.about.goal_eyebrow} onChange={(e) => patch("about", "goal_eyebrow", e.target.value)} className={inputCls} /></Field>
          <Field label="Goal Heading"><input value={pages.about.goal_heading} onChange={(e) => patch("about", "goal_heading", e.target.value)} className={inputCls} /></Field>
        </div>
        <Field label="Goal Body"><textarea rows={2} value={pages.about.goal_body} onChange={(e) => patch("about", "goal_body", e.target.value)} className={inputCls} /></Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="'Why we need help' Heading"><input value={pages.about.why_heading} onChange={(e) => patch("about", "why_heading", e.target.value)} className={inputCls} /></Field>
          <Field label="'How you can help' Heading"><input value={pages.about.how_heading} onChange={(e) => patch("about", "how_heading", e.target.value)} className={inputCls} /></Field>
        </div>
        <Field label="'Why' Items" hint="One per line, formatted as Label: description"><textarea rows={4} value={pages.about.why_items.join("\n")} onChange={(e) => patch("about", "why_items", linesToArray(e.target.value))} className={inputCls} /></Field>
        <Field label="'How' Intro"><textarea rows={2} value={pages.about.how_intro} onChange={(e) => patch("about", "how_intro", e.target.value)} className={inputCls} /></Field>
        <Field label="'How' Items" hint="One per line, formatted as Label: description"><textarea rows={3} value={pages.about.how_items.join("\n")} onChange={(e) => patch("about", "how_items", linesToArray(e.target.value))} className={inputCls} /></Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Closing Eyebrow"><input value={pages.about.closing_eyebrow} onChange={(e) => patch("about", "closing_eyebrow", e.target.value)} className={inputCls} /></Field>
          <Field label="Closing Heading"><input value={pages.about.closing_heading} onChange={(e) => patch("about", "closing_heading", e.target.value)} className={inputCls} /></Field>
        </div>
        <Field label="Closing Body" hint="Separate paragraphs with a blank line"><textarea rows={3} value={pages.about.closing_body} onChange={(e) => patch("about", "closing_body", e.target.value)} className={inputCls} /></Field>
        <ImageField label="Closing Section Image" value={pages.about.closing_image_url} onChange={(v) => patch("about", "closing_image_url", v)} testid="about-closing-image" />

        <Field label="Gallery Images" hint="One image URL per line"><textarea rows={3} value={pages.about.images.join("\n")} onChange={(e) => patch("about", "images", linesToArray(e.target.value))} className={inputCls} /></Field>
      </Section>

      <Section title="Contact Us Page">
        <Field label="Hero Heading"><input value={pages.contact.hero_heading} onChange={(e) => patch("contact", "hero_heading", e.target.value)} className={inputCls} /></Field>
        <Field label="Hero Subheading"><textarea rows={2} value={pages.contact.hero_subheading} onChange={(e) => patch("contact", "hero_subheading", e.target.value)} className={inputCls} /></Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Eyebrow"><input value={pages.contact.eyebrow} onChange={(e) => patch("contact", "eyebrow", e.target.value)} className={inputCls} /></Field>
          <Field label="Heading"><input value={pages.contact.heading} onChange={(e) => patch("contact", "heading", e.target.value)} className={inputCls} /></Field>
        </div>
        <Field label="Address"><textarea rows={2} value={pages.contact.address} onChange={(e) => patch("contact", "address", e.target.value)} className={inputCls} /></Field>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Form Heading"><input value={pages.contact.form_heading} onChange={(e) => patch("contact", "form_heading", e.target.value)} className={inputCls} /></Field>
          <Field label="Form Intro"><input value={pages.contact.form_intro} onChange={(e) => patch("contact", "form_intro", e.target.value)} className={inputCls} /></Field>
        </div>
        <Field label="Google Maps Embed URL (optional)" hint="From Google Maps > Share > Embed a map"><input value={pages.contact.map_embed_url} onChange={(e) => patch("contact", "map_embed_url", e.target.value)} className={inputCls} /></Field>
        <p className="text-xs text-[#56624F]">Email and phone shown on this page come from Branding settings (org_email / org_phone).</p>
      </Section>
    </div>
  );
}
